import { removePersonalAnthropicApiKey } from './personal-api-key';
import type { Bootstrap, TokenPair } from './api-types';

export interface StoredSession {
  tokens: TokenPair;
  bootstrap: Bootstrap;
}

const STORAGE_KEY = 'resonance.session.v1';

/**
 * The session is cached in memory so the hot path (every request builds an
 * Authorization header) never touches localStorage, and mirrored to
 * localStorage so a reload keeps you signed in.
 */
let cached: StoredSession | null | undefined;

export function loadSession(): StoredSession | null {
  if (cached !== undefined) return cached;
  cached = readStorage();
  return cached;
}

function readStorage(): StoredSession | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    return parsed.tokens?.accessToken?.token ? parsed : null;
  } catch {
    return null;
  }
}

const listeners = new Set<(session: StoredSession | null) => void>();

export function saveSession(session: StoredSession): void {
  cached = session;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    /* Private-mode storage failures leave the in-memory session working. */
  }
  for (const listener of listeners) listener(session);
}

export function clearSession(): void {
  cached = null;
  try {
    // Legacy browser credentials were unscoped. Do not carry one into another account.
    removePersonalAnthropicApiKey();
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* Nothing to recover from — the in-memory session is already cleared. */
  }
  for (const listener of listeners) listener(null);
}

export function subscribeToSession(listener: (session: StoredSession | null) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
